// Outglow web realtime client — singleton WebSocket, shared across
// pages via subscribe(). Mirrors mobile/src/services/realtime.js.

import { tokenStore, SERVER_BASE_URL } from '../api/client';

const RECONNECT_BASE_MS = 800;
const RECONNECT_MAX_MS = 30 * 1000;
const HEARTBEAT_MS = 25 * 1000;

// Web bundle runs on the same origin as the API when VITE_API_BASE_URL
// is unset (dev mode). SERVER_BASE_URL is empty in that case, so we
// fall back to window.location.origin so the WS hits ws://localhost.
function resolveServerOrigin() {
  if (SERVER_BASE_URL) return SERVER_BASE_URL;
  if (typeof window !== 'undefined') return window.location.origin;
  return '';
}

let ws = null;
let connecting = null;
let topics = new Map();
let reconnectAttempt = 0;
let reconnectTimer = null;
let heartbeatTimer = null;
let intentionallyClosed = false;

function wsUrl(token) {
  const origin = resolveServerOrigin();
  const base = origin.replace(/^http/, 'ws');
  return `${base}/realtime?token=${encodeURIComponent(token)}`;
}

function clearTimers() {
  if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
  if (heartbeatTimer) { clearInterval(heartbeatTimer); heartbeatTimer = null; }
}

function scheduleReconnect() {
  if (intentionallyClosed) return;
  if (reconnectTimer) return;
  const wait = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * (2 ** reconnectAttempt));
  reconnectAttempt += 1;
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    connect().catch(() => scheduleReconnect());
  }, wait);
}

function connect() {
  if (ws && ws.readyState === WebSocket.OPEN) return Promise.resolve(ws);
  if (connecting) return connecting;
  // Check the token BEFORE creating the promise. Doing it inside the
  // executor was a permanent-failure trap: the executor runs
  // synchronously, so `connecting = null` ran first and was then
  // immediately overwritten by the outer `connecting = <promise>`
  // assignment. No socket was constructed, so neither onopen nor
  // onclose could ever clear it again — leaving `connecting` as a
  // rejected promise that the `if (connecting) return connecting;`
  // guard above returned forever. One connect() before the token was
  // available killed realtime for the rest of the tab's life.
  const token = tokenStore.get();
  if (!token) return Promise.reject(new Error('No token'));

  connecting = new Promise((resolve, reject) => {
    const socket = new WebSocket(wsUrl(token));
    let settled = false;
    socket.onopen = () => {
      ws = socket;
      connecting = null;
      reconnectAttempt = 0;
      // Re-subscribe to every topic we're watching after a reconnect.
      for (const topic of topics.keys()) {
        try { socket.send(JSON.stringify({ op: 'subscribe', topic })); } catch {}
      }
      if (heartbeatTimer) clearInterval(heartbeatTimer);
      heartbeatTimer = setInterval(() => {
        if (socket.readyState !== WebSocket.OPEN) return;
        try { socket.send(JSON.stringify({ op: 'ping' })); } catch {}
      }, HEARTBEAT_MS);
      settled = true;
      resolve(socket);
    };
    socket.onmessage = (event) => {
      let msg;
      try { msg = JSON.parse(event.data); } catch { return; }
      if (!msg || typeof msg.op !== 'string') return;
      if (msg.op === 'event' && msg.topic) {
        const set = topics.get(msg.topic);
        if (!set) return;
        for (const handler of set) {
          try { handler(msg.data); } catch (err) { console.warn('[realtime] handler error', err); }
        }
      }
    };
    socket.onerror = () => { /* close handler reacts */ };
    socket.onclose = () => {
      // Only tear down if this is still the live socket — a late close
      // from a superseded one (server restart, network flap, tab wake)
      // would otherwise null out a healthy connection and stop its
      // heartbeat. Ported from mobile, which fixed this first.
      if (!(ws === socket || ws === null)) {
        if (!settled) { settled = true; reject(new Error('Closed before open')); }
        return;
      }
      ws = null;
      connecting = null;
      clearTimers();
      if (!settled) { settled = true; reject(new Error('Closed before open')); }
      scheduleReconnect();
    };
  });
  return connecting;
}

export function subscribe(topic, handler) {
  if (!topic || typeof handler !== 'function') return () => {};
  let set = topics.get(topic);
  if (!set) { set = new Set(); topics.set(topic, set); }
  set.add(handler);
  intentionallyClosed = false;
  connect()
    .then((socket) => {
      if (socket.readyState === WebSocket.OPEN) {
        try { socket.send(JSON.stringify({ op: 'subscribe', topic })); } catch {}
      }
    })
    // Arm a retry rather than assuming one is running. connect() rejects
    // BEFORE any socket exists when no token is readable yet, so onclose
    // never fires and nothing else would ever schedule one — the topic
    // this component just registered would never reach the gateway, for
    // the life of the mount. Ported from mobile.
    .catch(() => { scheduleReconnect(); });
  return () => {
    const current = topics.get(topic);
    if (!current) return;
    current.delete(handler);
    if (current.size === 0) {
      topics.delete(topic);
      if (ws && ws.readyState === WebSocket.OPEN) {
        try { ws.send(JSON.stringify({ op: 'unsubscribe', topic })); } catch {}
      }
    }
  };
}

export function disconnect() {
  intentionallyClosed = true;
  clearTimers();
  topics.clear();
  if (ws) { try { ws.close(1000, 'logout'); } catch {} ws = null; }
  connecting = null;
}
